import React, { useEffect, useRef } from "react";
import Particles from "@tsparticles/react";
import { loadFull } from "tsparticles";
import { motion, useAnimation } from "framer-motion";

const FactsSection = () => {
  const particlesInit = async (main) => {
    await loadFull(main);
  };

  const controls = useAnimation();
  const sectionRef = useRef(null);

  // refs pour les compteurs
  const counterRefs = useRef([]);
  counterRefs.current = [];

  const addToRefs = (el) => {
    if (el && !counterRefs.current.includes(el)) {
      counterRefs.current.push(el);
    }
  };

  // Animation de compteur avec easing
  const animateCount = (el, target, duration = 2000) => {
    let start = null;
    const initial = 0;
    const step = (timestamp) => {
      if (!start) start = timestamp;
      const progress = Math.min((timestamp - start) / duration, 1);
      const easeOutQuart = 1 - Math.pow(1 - progress, 4);
      const current = Math.floor(initial + (target - initial) * easeOutQuart);
      el.textContent = current.toLocaleString();
      if (progress < 1) {
        requestAnimationFrame(step);
      } else {
        el.textContent = target.toLocaleString();
        el.style.transform = 'scale(1.05)';
        setTimeout(() => { el.style.transform = 'scale(1)'; }, 200);
      }
    };
    requestAnimationFrame(step);
  };

  useEffect(() => {
    const observerOptions = {
      root: null,
      rootMargin: "0px",
      threshold: 0.2,
    };
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          controls.start("visible");
          entry.target.querySelectorAll('.counter-element').forEach((el, index) => {
            setTimeout(() => {
              const target = parseInt(el.getAttribute("data-count"), 10) || 0;
              animateCount(el, target, 1800);
            }, index * 300);
          });
        }
      });
    }, observerOptions);

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }
    return () => observer.disconnect();
  }, [controls]);

  const factsData = [
    { icon: "images/icon-image/fact1.png", count: 162, title: "Clients VIP" },
    { icon: "images/icon-image/fact2.png", count: 3647, title: "Clients satisfaits" },
    { icon: "images/icon-image/fact3.png", count: 6, title: "Services premium", plus: true },
    { icon: "images/icon-image/fact4.png", count: 27, title: "Pays d'expérience" },
  ];

  // Couleurs du thème
  const colors = {
    bg: '#FDF8F0',
    gold: '#C69C6D',
    goldDark: '#B88A56',
    goldLight: '#F5E1C8',
    text: '#3B2F2A',
    textSoft: '#5C4A3D',
    cardBg: 'rgba(255,255,255,0.75)',
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { staggerChildren: 0.2, delayChildren: 0.1 } }
  };

  const cardVariants = {
    hidden: { opacity: 0, y: 40, scale: 0.9 },
    visible: { opacity: 1, y: 0, scale: 1, transition: { type: "spring", damping: 15, stiffness: 100 } },
    hover: {
      y: -10,
      boxShadow: "0 20px 40px rgba(184, 138, 86, 0.25)",
      transition: { type: "spring", damping: 15, stiffness: 300 }
    }
  };

  const iconVariants = {
    hover: {
      scale: 1.1,
      rotate: 360,
      transition: { type: "spring", damping: 10, stiffness: 200 }
    }
  };

  // Particules subtiles dorées
  const particlesOptions = {
    fullScreen: { enable: false },
    fpsLimit: 60,
    particles: {
      number: { value: 50, density: { enable: true, area: 800 } },
      color: { value: ["#C69C6D", "#F5E1C8", "#B88A56"] },
      shape: { type: "circle" },
      opacity: { value: 0.2, random: { enable: true, minimumValue: 0.05 } },
      size: { value: { min: 1, max: 4 } },
      links: {
        enable: true,
        distance: 150,
        color: "#C69C6D",
        opacity: 0.1,
        width: 1,
        triangles: { enable: false }
      },
      move: {
        enable: true,
        speed: 0.8,
        direction: "none",
        random: true,
        outModes: { default: "bounce" },
        attract: { enable: true, rotateX: 600, rotateY: 1200 }
      }
    },
    interactivity: {
      events: {
        onHover: { enable: true, mode: "repulse", parallax: { enable: true, force: 30, smooth: 10 } },
        resize: true,
      },
      modes: {
        repulse: { distance: 80, duration: 0.4 }
      }
    },
    detectRetina: true,
  };

  return (
    <section ref={sectionRef} className="facts-section" aria-labelledby="facts-heading">
      {/* Fond avec particules */}
      <div className="particles-container">
        <Particles init={particlesInit} options={particlesOptions} className="particles-canvas" />
      </div>

      <div className="content-container">
        <motion.div
          className="header-content"
          initial={{ opacity: 0, y: -30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
        >
          <h2 id="facts-heading" className="main-title">
            Excellence en Chiffres
          </h2>
          <p className="subtitle">
            Des années d'expérience traduites en résultats concrets et mesurables
          </p>
        </motion.div>

        <motion.div
          className="cards-grid"
          variants={containerVariants}
          initial="hidden"
          animate={controls}
        >
          {factsData.map((fact, index) => (
            <motion.div
              key={index}
              className="fact-card"
              variants={cardVariants}
              whileHover="hover"
              whileTap={{ scale: 0.97 }}
              tabIndex={0}
              role="article"
              aria-label={`${fact.title}: ${fact.count}`}
            >
              {/* Icône */}
              <motion.div className="icon-container" variants={iconVariants}>
                <div className="icon-background" />
                <img src={fact.icon} alt="" className="fact-icon" loading="lazy" />
              </motion.div>

              {/* Compteur */}
              <div className="counter-container">
                <span className="counter-element" data-count={fact.count} ref={addToRefs}>0</span>
                {fact.plus && <span className="plus-symbol">+</span>}
              </div>

              {/* Titre */}
              <h3 className="fact-title">{fact.title}</h3>

              {/* Coins décoratifs */}
              <div className="card-corner top-left" />
              <div className="card-corner top-right" />
              <div className="card-corner bottom-left" />
              <div className="card-corner bottom-right" />
            </motion.div>
          ))}
        </motion.div>
      </div>

      <style>{`
        .facts-section {
          position: relative;
          min-height: 100vh;
          display: flex;
          align-items: center;
          background: #FDF8F0;
          overflow: hidden;
          padding: 80px 20px;
        }
        .particles-container {
          position: absolute;
          top: 0; left: 0; width: 100%; height: 100%;
          z-index: 1;
        }
        .particles-canvas {
          position: absolute;
          width: 100%; height: 100%;
        }
        .content-container {
          position: relative;
          z-index: 3;
          max-width: 1200px;
          margin: 0 auto;
          width: 100%;
        }
        .header-content {
          text-align: center;
          margin-bottom: 60px;
        }
        .main-title {
          font-size: clamp(2rem, 5vw, 3rem);
          font-weight: 700;
          color: #3B2F2A;
          margin-bottom: 0.5rem;
          letter-spacing: -0.02em;
        }
        .subtitle {
          font-size: 1.1rem;
          color: #5C4A3D;
          margin: 0;
          font-weight: 400;
        }
        .cards-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
          gap: 28px;
        }
        .fact-card {
          position: relative;
          padding: 40px 24px;
          border-radius: 28px;
          background: linear-gradient(135deg, #FFFFFF 0%, #FDF8F0 100%);
          border: 1px solid rgba(198, 156, 109, 0.25);
          box-shadow: 0 8px 24px rgba(107, 90, 70, 0.06);
          backdrop-filter: blur(10px);
          text-align: center;
          cursor: pointer;
          transition: all 0.3s ease;
          overflow: hidden;
        }
        /* Dégradé doré au survol */
        .fact-card::after {
          content: '';
          position: absolute;
          inset: 0;
          border-radius: 28px;
          background: linear-gradient(135deg, #EAD5C0 0%, #D4B895 40%, #C69C6D 100%);
          opacity: 0;
          transition: opacity 0.4s;
          z-index: 0;
        }
        .fact-card:hover::after {
          opacity: 0.2;
        }
        .fact-card > * {
          position: relative;
          z-index: 1;
        }
        .icon-container {
          width: 80px;
          height: 80px;
          margin: 0 auto 20px;
          display: flex;
          align-items: center;
          justify-content: center;
          position: relative;
        }
        .icon-background {
          position: absolute;
          width: 100%; height: 100%;
          border-radius: 50%;
          background: rgba(198, 156, 109, 0.1);
          backdrop-filter: blur(6px);
          border: 1px solid rgba(198, 156, 109, 0.2);
        }
        .fact-icon {
          width: 50px;
          height: 50px;
          z-index: 2;
          filter: drop-shadow(0 4px 8px rgba(0,0,0,0.1));
        }
        .counter-container {
          margin: 15px 0 5px;
        }
        .counter-element {
          font-size: 3rem;
          font-weight: 700;
          background: linear-gradient(135deg, #C69C6D, #B88A56);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
          display: inline-block;
          transition: all 0.3s;
        }
        .plus-symbol {
          font-size: 2rem;
          color: #C69C6D;
          margin-left: 5px;
          font-weight: 300;
        }
        .fact-title {
          font-size: 1.1rem;
          font-weight: 600;
          color: #5C4A3D;
          margin: 0;
          letter-spacing: 0.03em;
          text-transform: uppercase;
        }
        /* Coins décoratifs */
        .card-corner {
          position: absolute;
          width: 16px; height: 16px;
          opacity: 0;
          transition: opacity 0.3s;
          z-index: 2;
        }
        .top-left    { top: 8px;  left: 8px;  border-top: 2px solid #C69C6D; border-left: 2px solid #C69C6D; border-radius: 6px 0 0 0; }
        .top-right   { top: 8px;  right: 8px; border-top: 2px solid #C69C6D; border-right: 2px solid #C69C6D; border-radius: 0 6px 0 0; }
        .bottom-left { bottom: 8px; left: 8px;  border-bottom: 2px solid #C69C6D; border-left: 2px solid #C69C6D; border-radius: 0 0 0 6px; }
        .bottom-right{ bottom: 8px; right: 8px; border-bottom: 2px solid #C69C6D; border-right: 2px solid #C69C6D; border-radius: 0 0 6px 0; }
        .fact-card:hover .card-corner { opacity: 1; }

        @media (max-width: 768px) {
          .facts-section { padding: 60px 16px; }
          .cards-grid { grid-template-columns: 1fr; gap: 20px; }
        }
      `}</style>
    </section>
  );
};

export default FactsSection;